<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { ArrowUpRight, Check, ClipboardCopy, Clock3, ExternalLink, Eye, Image, Layers3, Share2, ShieldCheck, Sparkles, Star, UsersRound, WifiOff, X } from '@lucide/vue'
import type { SurveySummary } from '../types'

const props = defineProps<{ survey: SurveySummary; index?: number; rank?: number; compact?: boolean }>()
const shareOpen = ref(false)
const shareDialog = ref<HTMLElement | null>(null)
const shareMessage = ref('')
const shareUrl = computed(() => new URL(`/surveys/${encodeURIComponent(props.survey.slug)}`, window.location.origin).toString())
const localOnly = computed(() => ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname))
const canSystemShare = computed(() => typeof navigator.share === 'function')

const typeLabel: Record<string, string> = {
  psychology: '心理探索', research: '研究实验', general: '一般问卷', sensitive: '敏感研究',
}

async function copyShareUrl() {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(shareUrl.value)
    return
  }
  const field = document.createElement('textarea')
  field.value = shareUrl.value
  field.setAttribute('readonly', '')
  field.style.position = 'fixed'
  field.style.opacity = '0'
  document.body.appendChild(field)
  field.select()
  const copied = document.execCommand('copy')
  field.remove()
  if (!copied) throw new Error('clipboard unavailable')
}

function openShareDialog() {
  shareMessage.value = ''
  shareOpen.value = true
  void nextTick(() => shareDialog.value?.focus())
}

function closeShareDialog() {
  shareOpen.value = false
}

async function copyFromDialog() {
  try {
    await copyShareUrl()
    shareMessage.value = localOnly.value
      ? '本机填写链接已复制；它只能在当前电脑的网站服务运行时打开。'
      : '填写链接已复制，可以发送给填写者。'
  } catch {
    shareMessage.value = '浏览器未允许自动复制，请选中上方链接后手动复制。'
  }
}

async function shareFromDialog() {
  const text = `邀请你填写《${props.survey.title}》。中途退出后，用同一浏览器重新打开链接即可继续。`
  try {
    await navigator.share({ title: `邀请填写：${props.survey.title}`, text, url: shareUrl.value })
    shareMessage.value = '已打开系统分享面板。'
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      shareMessage.value = '已取消系统分享。'
      return
    }
    shareMessage.value = '系统分享暂不可用，可以改用“复制链接”。'
  }
}
</script>

<template>
  <article :class="['survey-card community-topic-card', { compact }]" :style="{ '--accent': survey.accent, '--delay': `${(index || 0) * 60}ms` }">
    <RouterLink class="card-open-link" :to="`/surveys/${survey.slug}`" :aria-label="`查看问卷：${survey.title}`">
      <div class="card-topline">
        <span v-if="rank" class="hot-rank">{{ String(rank).padStart(2, '0') }}</span>
        <span class="type-pill"><Sparkles :size="13"/>{{ typeLabel[survey.type] }}</span>
        <ArrowUpRight :size="20" class="card-arrow"/>
      </div>
      <div class="topic-cover"><span>{{ survey.title.slice(0, 1) }}</span><small>{{ survey.category }}</small></div>
      <div class="topic-copy">
        <div class="topic-labels">
          <span class="result-mode-pill" :class="survey.resultMode === 'scores_only' ? 'scores' : 'profile'"><Layers3 v-if="survey.resultMode === 'scores_only'"/><Image v-else/>{{ survey.resultMode === 'scores_only' ? '无 IP · 纯维度' : '含 IP 角色' }}</span>
          <span v-if="survey.communityZone === 'research'" class="impact-mini"><Star/>影响 {{ survey.impactLevel || 1 }}/5 · {{ survey.selfAssessmentVerified ? '已核验' : '历史版' }}</span>
          <span v-else class="ai-pass"><ShieldCheck/>站长审核</span>
        </div>
        <h3>{{ survey.title }}</h3>
        <p class="card-description">{{ survey.communityIntro || survey.description }}</p>
        <p class="topic-author">由 {{ survey.creatorName || '受邀创作者' }} 创作 · 站长审核入馆</p>
      </div>
      <div class="card-meta">
        <span><Clock3/>{{ survey.estimatedMinutes }}分钟</span>
        <span><UsersRound/>{{ survey.responseCount }}作答</span>
        <span><Eye/>{{ survey.viewCount || 0 }}浏览</span>
      </div>
    </RouterLink>
    <button class="card-share" type="button" title="打开填写与分享选项" :aria-label="`打开《${survey.title}》的填写与分享选项`" @click="openShareDialog"><Share2/><span>分享填写</span></button>
  </article>

  <Teleport to="body">
    <div v-if="shareOpen" ref="shareDialog" class="modal-backdrop community-share-backdrop" tabindex="-1" @click.self="closeShareDialog" @keydown.esc="closeShareDialog">
      <section class="community-share-dialog" role="dialog" aria-modal="true" :aria-labelledby="`community-share-title-${survey.id}`">
        <button class="modal-close" type="button" aria-label="关闭分享窗口" title="关闭" @click="closeShareDialog"><X/></button>
        <p class="eyebrow"><Share2/>SHARE SURVEY</p>
        <h2 :id="`community-share-title-${survey.id}`">分享《{{ survey.title }}》</h2>
        <p>先打开填写页确认内容，也可以复制链接或调用系统分享。</p>

        <label class="share-link-label">填写链接</label>
        <div class="share-link-field">
          <input :value="shareUrl" readonly aria-label="问卷填写链接" @focus="($event.target as HTMLInputElement).select()">
          <button type="button" @click="copyFromDialog"><ClipboardCopy/>复制链接</button>
        </div>

        <p v-if="localOnly" class="share-local-notice"><WifiOff/><span><strong>当前是本机测试地址。</strong><br>你可以在这台电脑上预览填写页，但别人无法通过这条 127.0.0.1 链接访问；正式部署域名后，这里会自动生成可分享的公网链接。</span></p>
        <p v-if="shareMessage" class="community-share-feedback" role="status"><Check/>{{ shareMessage }}</p>

        <div class="community-share-actions">
          <RouterLink class="button button-ghost" :to="`/surveys/${survey.slug}`" target="_blank" rel="noopener" @click="closeShareDialog"><ExternalLink/>打开填写页</RouterLink>
          <button class="button button-dark" type="button" @click="copyFromDialog"><ClipboardCopy/>复制链接</button>
          <button v-if="canSystemShare && !localOnly" class="button button-dark" type="button" @click="shareFromDialog"><Share2/>系统分享</button>
        </div>
      </section>
    </div>
  </Teleport>
</template>
